import type { i18n } from "i18next";
import { useTranslation } from "react-i18next";
import { useLanguageStore, type Language } from "@nasam/core";
import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@nasam/ui";

const FLAG_SRC: Record<Language, string> = {
  ar: "/brand/flag-sa.svg",
  en: "/brand/flag-gb.svg",
};

export async function switchAppLanguage(
  i18nInstance: i18n,
  targetLanguage: Language,
): Promise<void> {
  useLanguageStore.getState().setLanguage(targetLanguage);
  await i18nInstance.changeLanguage(targetLanguage);
}

export interface LanguageSwitcherProps {
  /** Callers paint their own controls, so the trigger takes its styling. */
  readonly className?: string | undefined;
  readonly tooltipSide?: "top" | "right" | "bottom" | "left" | undefined;
  /** Override when a second instance renders alongside the default one (e.g. TopBar's mobile header). */
  readonly testId?: string | undefined;
}

export function LanguageSwitcher({
  className,
  tooltipSide = "right",
  testId = "language-switcher",
}: LanguageSwitcherProps = {}) {
  const { i18n } = useTranslation();
  const currentLanguage = useLanguageStore((state) => state.language);
  const nextLanguage: Language = currentLanguage === "en" ? "ar" : "en";
  const label = currentLanguage === "en" ? "العربية" : "English";

  const handleToggle = async () => {
    await switchAppLanguage(i18n, nextLanguage);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => void handleToggle()}
          aria-label={label}
          data-testid={testId}
          data-current-language={currentLanguage}
          className={cn(
            "flex size-10 items-center justify-center rounded-lg text-secondary-icon transition-colors duration-150 ease-emphasized hover:bg-container-hover hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
            className,
          )}
        >
          <img
            src={FLAG_SRC[currentLanguage]}
            alt=""
            className="size-5 shrink-0 rounded-full"
          />
          <span className="sr-only">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </Tooltip>
  );
}
